import { useEffect, useState } from 'react';
import { platformApi } from '../lib/platformApi/client';
import { usePlatformAuth } from './PlatformAuthProvider';
import type { PlatformTenant } from './types';

const STATUS_STYLE: Record<PlatformTenant['status'], { bg: string; color: string; label: string }> = {
  active:       { bg: '#DCFCE7', color: '#166534', label: 'Active' },
  provisioning: { bg: '#FEF3C7', color: '#92400E', label: 'Provisioning' },
  suspended:    { bg: '#FEE2E2', color: '#991B1B', label: 'Suspended' },
  deleted:      { bg: '#F3F4F6', color: '#6B7280', label: 'Deleted' },
};

interface TenantListScreenProps {
  onOpenTenant: (id: number) => void;
  onProvisionNew: () => void;
  onOpenAiProviders: () => void;
  onOpenAiStatements: () => void;
  onOpenFeedback: () => void;
  onOpenRequirements: () => void;
  onOpenPlansPricing: () => void;
  onOpenEnquiries: () => void;
}

export default function TenantListScreen({ onOpenTenant, onProvisionNew, onOpenAiProviders, onOpenAiStatements, onOpenFeedback, onOpenRequirements, onOpenPlansPricing, onOpenEnquiries }: TenantListScreenProps) {
  const { operator, logout } = usePlatformAuth();
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = () => {
    setLoading(true);
    platformApi.get<PlatformTenant[]>('/tenants')
      .then(setTenants)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const [openFeedback, setOpenFeedback] = useState(0);
  const [newEnquiries, setNewEnquiries] = useState(0);
  useEffect(() => {
    platformApi.get<{ open: number }>('/feedback/summary').then(s => setOpenFeedback(s.open)).catch(() => {});
    platformApi.get<{ new: number }>('/enquiries/summary').then(s => setNewEnquiries(s.new)).catch(() => {});
  }, []);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>Tenants</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>Signed in as {operator?.full_name}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={onOpenFeedback} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
            💬 Feedback{openFeedback > 0 && <span style={{ marginLeft: 6, background: '#DC2626', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700 }}>{openFeedback}</span>}
          </button>
          <button onClick={onOpenEnquiries} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
            📨 Enquiries{newEnquiries > 0 && <span style={{ marginLeft: 6, background: '#DC2626', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: 11, fontWeight: 700 }}>{newEnquiries}</span>}
          </button>
          <button onClick={onOpenRequirements} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>📋 Requirements</button>
          <button onClick={onOpenPlansPricing} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>💲 Plans &amp; pricing</button>
          <button onClick={onOpenAiProviders} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>🤖 AI providers</button>
          <button onClick={onOpenAiStatements} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>🧾 AI statements</button>
          <button className="btn-primary" onClick={onProvisionNew}>+ Provision new tenant</button>
          <button onClick={logout} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>Sign out</button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div className="card" style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #E5E7EB', color: '#6B7280' }}>
              <th style={{ padding: '10px 14px' }}>Slug</th>
              <th style={{ padding: '10px 14px' }}>Name</th>
              <th style={{ padding: '10px 14px' }}>Status</th>
              <th style={{ padding: '10px 14px' }}>Plan</th>
              <th style={{ padding: '10px 14px' }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>Loading…</td></tr>
            )}
            {!loading && tenants.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>No tenants yet.</td></tr>
            )}
            {tenants.map(t => {
              const s = STATUS_STYLE[t.status];
              return (
                <tr key={t.id} onClick={() => onOpenTenant(t.id)} style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                  <td style={{ padding: '10px 14px', fontFamily: 'var(--mono)' }}>{t.slug}</td>
                  <td style={{ padding: '10px 14px' }}>{t.name}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: s.bg, color: s.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{s.label}</span>
                  </td>
                  <td style={{ padding: '10px 14px' }}>{t.plan}</td>
                  <td style={{ padding: '10px 14px', color: '#6B7280' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
